import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

export default function ProjectTimeline({ board, groups, items, members, api, onDataChange }) {
  // Filter items that have dates
  const itemsWithDates = items.filter(item => 
    item.column_values['due_date'] || item.column_values['start_date']
  );

  // Sort by due date
  const sortedItems = itemsWithDates.sort((a, b) => {
    const dateA = new Date(a.column_values['due_date'] || a.created_at);
    const dateB = new Date(b.column_values['due_date'] || b.created_at);
    return dateA - dateB;
  });

  const getStatusColor = (status) => {
    const colors = {
      'Not Started': 'bg-gray-100 text-gray-800',
      'Working on it': 'bg-orange-100 text-orange-800',
      'Done': 'bg-green-100 text-green-800',
      'Stuck': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Timeline do Projeto</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sortedItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nenhum item com data definida</p>
              <p className="text-sm">Adicione datas aos itens para visualizar a timeline</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedItems.map((item) => {
                const group = groups.find(g => g.id === item.group_id);
                const status = item.column_values['status'] || 'Not Started';
                const assignee = item.column_values['person'];
                const dueDate = item.column_values['due_date'];
                const priority = item.column_values['priority'];

                return (
                  <div 
                    key={item.id} 
                    className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                  >
                    {/* Timeline Line */}
                    <div 
                      className="w-1 h-16 rounded-full"
                      style={{ backgroundColor: group?.color || '#6B7280' }}
                    />

                    {/* Item Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900 truncate">
                          {item.name}
                        </h4>
                        <div className="flex items-center space-x-2 ml-4">
                          {priority && (
                            <Badge 
                              variant="outline" 
                              className={priority === 'High' || priority === 'Critical' ? 'text-red-600' : ''}
                            >
                              {priority}
                            </Badge>
                          )}
                          <Badge className={getStatusColor(status)}>
                            {status}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span className="font-medium" style={{ color: group?.color }}>
                            {group?.title}
                          </span>
                          {assignee && (
                            <div className="flex items-center space-x-1">
                              <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center text-xs text-white">
                                {assignee.charAt(0).toUpperCase()}
                              </div>
                              <span>{assignee}</span>
                            </div>
                          )}
                        </div>

                        {dueDate && (
                          <div className={`text-sm flex items-center space-x-1 ${
                            isOverdue(dueDate) ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>
                              {new Date(dueDate).toLocaleDateString()}
                              {isOverdue(dueDate) && ' (Atrasado)'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}